import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input } from '@angular/core';
import { MemberByCode, MemberCollaboration } from 'src/app/shared/models/impact-panel.model';
import { ImpactPanelService } from 'src/app/shared/services/impact-panel.service';
import {
  ActivityIconKind,
  categoryIconKind,
  categoryLabel,
  collaborationStatusMeta,
  fmtDate,
  fmtDateTime,
  isGhostMemberCollaboration,
  otherMemberIdOf,
  txHashFromExplorer,
} from 'src/app/shared/utils/impact-panel.utils';

@Component({
  selector: 'app-collaboration-row',
  templateUrl: './collaboration-row.component.html',
  styleUrls: ['./collaboration-row.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CollaborationRowComponent {
  @Input() collaboration!: MemberCollaboration;

  expanded = false;
  loadingMember = false;
  memberLoaded = false;
  otherMember: MemberByCode | null = null;

  constructor(
    private impactPanelService: ImpactPanelService,
    private cdr: ChangeDetectorRef,
  ) {}

  get sent(): boolean {
    return this.collaboration.direction === 'sent';
  }

  get category(): string {
    return categoryLabel(this.collaboration.category);
  }

  get categoryKind(): ActivityIconKind {
    return categoryIconKind(this.collaboration.category);
  }

  get statusMeta() {
    return this.collaboration.deleted
      ? { text: 'Eliminada', cssClass: 'def' }
      : collaborationStatusMeta(this.collaboration);
  }

  get documentCount(): number {
    const n = Number(this.collaboration.documentCount ?? 0);
    return Number.isFinite(n) ? n : 0;
  }

  get commentCount(): number {
    const n = Number(this.collaboration.commentCount ?? 0);
    return Number.isFinite(n) ? n : 0;
  }

  get commentLabel(): string {
    return this.commentCount === 1 ? 'comentario' : 'comentarios';
  }

  get otherCode(): number | string {
    return otherMemberIdOf(this.collaboration) ?? '—';
  }

  get canResolveMember(): boolean {
    return (
      otherMemberIdOf(this.collaboration) !== undefined &&
      !isGhostMemberCollaboration(this.collaboration)
    );
  }

  // El otro miembro se consulta (`/members/:memberCode`) recién al expandir la tarjeta.
  get member(): MemberByCode | null {
    return this.otherMember;
  }

  get otherMemberName(): string {
    const member = this.member;
    if (!member) return '';
    return [member.name, member.lastName].filter(Boolean).join(' ').trim();
  }

  get otherMemberType(): string {
    const type = (this.member?.type || '').toUpperCase();
    if (type === 'PERSON') return 'Persona';
    if (type === 'COMPANY') return 'Empresa';
    return '';
  }

  get otherMemberCommunities(): string {
    return (this.member?.communities || []).join(', ');
  }

  toggle(): void {
    this.expanded = !this.expanded;
    if (
      this.expanded &&
      this.canResolveMember &&
      !this.member &&
      !this.memberLoaded &&
      !this.loadingMember
    ) {
      this.loadOtherMember();
    }
  }

  private loadOtherMember(): void {
    this.loadingMember = true;
    this.impactPanelService.getMemberByCode(otherMemberIdOf(this.collaboration)!).subscribe(member => {
      this.otherMember = member;
      // Si falla (null) no se marca como cargado, así el próximo expand reintenta.
      this.memberLoaded = !!member;
      this.loadingMember = false;
      this.cdr.markForCheck();
    });
  }

  get lastDate(): string {
    return fmtDate(this.collaboration.date);
  }

  get lastDateTime(): string {
    return fmtDateTime(this.collaboration.date);
  }

  get txHash(): string | undefined {
    return txHashFromExplorer(this.collaboration.explorerUrl, this.collaboration.txHash);
  }
}
